import apiClient from './api'
import type { LabFile, LabFileListResponse, FileUploadResponse } from '@/types/file'

export interface UploadFileParams {
  file: File
  patientId: string
  labResultId?: string
  description?: string
}

export interface FileUploadProgress {
  loaded: number
  total: number
  percentage: number
}

export const fileApi = {
  /**
   * Upload a file for a patient
   */
  async uploadFile(
    params: UploadFileParams,
    onProgress?: (progress: FileUploadProgress) => void
  ): Promise<FileUploadResponse> {
    const formData = new FormData()
    formData.append('file', params.file)
    formData.append('patient_id', params.patientId)
    if (params.labResultId) {
      formData.append('lab_result_id', params.labResultId)
    }
    if (params.description) {
      formData.append('description', params.description)
    }

    const response = await apiClient.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          onProgress({
            loaded: progressEvent.loaded,
            total: progressEvent.total,
            percentage: Math.round((progressEvent.loaded * 100) / progressEvent.total),
          })
        }
      },
    })

    return response.data
  },

  /**
   * Get all files for a patient
   */
  async getPatientFiles(
    patientId: string,
    labResultId?: string
  ): Promise<LabFileListResponse> {
    const params: Record<string, string> = {}
    if (labResultId) {
      params.lab_result_id = labResultId
    }

    const response = await apiClient.get(`/files/patient/${patientId}`, { params })
    return response.data
  },

  /**
   * Get a single file by ID
   */
  async getFile(fileId: string): Promise<LabFile> {
    const response = await apiClient.get(`/files/${fileId}`)
    return response.data
  },

  /**
   * Get a fresh download URL for a file
   */
  async getDownloadUrl(fileId: string): Promise<string> {
    const response = await apiClient.get(`/files/${fileId}/download`)
    return response.data.download_url
  },

  /**
   * Delete a file
   */
  async deleteFile(fileId: string): Promise<void> {
    await apiClient.delete(`/files/${fileId}`)
  },

  /**
   * Get allowed file types
   */
  async getAllowedTypes(): Promise<{
    allowed_types: string[]
    allowed_mime_types: string[]
    max_size_mb: number
  }> {
    const response = await apiClient.get('/files/allowed-types')
    return response.data
  },
}
